import { CssBaseline } from '@material-ui/core';
import { createGenerateClassName, StylesProvider, ThemeProvider } from '@material-ui/core/styles';
import {
  DataLayerLastCustomURL,
  DataLayerLoginLogoutListener,
  DataLayerScrollEvent,
} from '@/features/shared';
import { withGTM } from '@/features/shared/HOCs';
import { create } from 'jss';
import preset from 'jss-preset-default';
import React, { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Route } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-last-location';
import { AppProvider } from '@/store';
import StateComponent from '@/store/StateComponent';
import { IS_PREVIEW } from '@/utils/constants';
import { mapTargetCTA, mapTargetLink, onDatalayer, onDataLayerCTA } from './App.datalayer';
import Page from './Page';
import { AuthProviders } from './providers/AuthProviders';
import theme from './theme';

const ScrollToTop = () => {
  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    if ('scrollRestoration' in history) {
      // eslint-disable-next-line no-restricted-globals
      history.scrollRestoration = 'manual';
    }
  }, []);

  return null;
};

const jss = create({
  ...preset(),
  insertionPoint: 'jss-insertion-point',
});

const generateClassName = createGenerateClassName({
  productionPrefix: 'gen-',
});

const App = () => {
  return (
    // issue: https://githubhot.com/repo/staylor/react-helmet-async/issues/163
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <StylesProvider jss={jss} generateClassName={generateClassName}>
            <AuthProviders isPreview={IS_PREVIEW}>
              <AppProvider>
                <LastLocationProvider>
                  <DataLayerLastCustomURL />
                  <ScrollToTop />
                  <CssBaseline />
                  <StateComponent />
                  <Route path="/(.*)" component={Page} />
                  <DataLayerScrollEvent />
                </LastLocationProvider>
              </AppProvider>
            </AuthProviders>
          </StylesProvider>
        </ThemeProvider>
      </HelmetProvider>
      <DataLayerLoginLogoutListener />
    </>
  );
};

const MappedAppClick = withGTM({
  Component: App,
  onDatalayer,
  config: { mapTarget: mapTargetLink },
});
const MappedAppCTA = withGTM({
  Component: MappedAppClick,
  onDatalayer: onDataLayerCTA,
  config: { mapTarget: mapTargetCTA },
});

export default React.memo(MappedAppCTA);

declare global {
  interface Window {
    isPreview: boolean;
    cmsBaseUrl: string;
    spaBaseUrl: string;
    prerenderReady: boolean;
    prerenderQueue: Array<string>;
    prerenderQueueTimer: any;
    webComponentsLoading: boolean;
    primaryFontFamily: string;
    secondaryFontFamily: string;
    adobeFontKitId: string;
    googleFontPrimary: string;
    googleFontSecondary: string;
    blockedVideoDomains: string[];
    ssoClientId: string;
    ssoAuthority: string;
    ssoBaseUrl: string;
    ssoRedirectPath: string;
    ssoPostLogoutRedirectPath: string;
    ssoScope: string;
    ssoSilentRedirectPath: string;
    ssoRoot: string;
    ssoExtraQueryParams: Record<string, string>;
    ssoUnauthorizedPath: string;
    ssoForbiddenPath: string;
    ssoPendingApprovalPath: string;
    powerAppsBaseUrl: string;
    powerAppsSignInUri: string;
    powerAppsSignOutUri: string;
    powerAppsMinutesExpiration: string;
    experlogixSignInUri: string;
    experlogixSignOutUri: string;
    experlogixMinutesExpiration: string;
    ssoContactRegistrationPath: string;
    ssoInvitedRegistrationPath: string;
    ssoOldPortalUrl: string;
    ssoBetaUserRedirectionEnabled: string;
    ssoAntiCache: boolean;
    dataLayer: any[];
    ssoUiLocale: string;
    MktoForms2: any;
    // Portal Redirects
    portalHomepageRedirects?: Record<string, string>;
    // Debug
    debugModeEnabled: boolean;
    OnetrustActiveGroups: string;
  }
}
